import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Shield, Users, DollarSign, ArrowRight, CheckCircle, Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
       setTestimonials(Array.isArray(response.data) ? response.data.slice(0, 3) : []);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      }
    };
    fetchTestimonials();
  }, []);

  const features = [
    {
      icon: <Zap className="w-10 h-10" />,
      title: 'High Quality Generators',
      description: 'Premium diesel generators from 5 kVA to 250 kVA capacity',
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: 'Fast Service Support',
      description: '24/7 emergency support and rapid response team',
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Experienced Team',
      description: 'Highly skilled technicians with years of expertise',
    },
    {
      icon: <DollarSign className="w-10 h-10" />,
      title: 'Affordable Pricing',
      description: 'Competitive rates with flexible payment options',
    },
  ];

  const services = [
    {
      title: 'Generator Sales',
      description: 'Wide range of diesel generators for all your power needs',
      image: 'https://images.unsplash.com/photo-1765897569589-effcccec14d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZGllc2VsJTIwZ2VuZXJhdG9yJTIwcG93ZXIlMjBwbGFudCUyMHNpdGV8ZW58MHx8fHwxNzcxOTEzMjM2fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Installation & Commissioning',
      description: 'Professional setup and testing for optimal performance',
      image: 'https://images.unsplash.com/photo-1601119462238-d7f347e41844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHx0ZWNobmljaWFuJTIwcmVwYWlyaW5nJTIwaW5kdXN0cmlhbCUyMGdlbmVyYXRvciUyMHNhZmV0eSUyMGdlYXJ8ZW58MHx8fHwxNzcxOTEzMjM3fDA&ixlib=rb-4.1.0&q=85',
    },
    {
      title: 'Annual Maintenance',
      description: 'Comprehensive AMC plans to keep your generator running',
      image: 'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
  ];

  const productCategories = [
    { range: '5-25 kVA', applications: 'Small Businesses, Homes', image: 'https://images.unsplash.com/photo-1683119926853-6427b2966f9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHw0fHxsYXJnZSUyMGluZHVzdHJpYWwlMjBkaWVzZWwlMjBnZW5lcmF0b3IlMjBtYWNoaW5lJTIwaXNvbGF0ZWR8ZW58MHx8fHwxNzcxOTEzMjU1fDA&ixlib=rb-4.1.0&q=85' },
    { range: '30-100 kVA', applications: 'Apartments, Offices', image: 'https://images.unsplash.com/photo-1704747081666-f4fd33d5db5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxsYXJnZSUyMGluZHVzdHJpYWwlMjBkaWVzZWwlMjBnZW5lcmF0b3IlMjBtYWNoaW5lJTIwaXNvbGF0ZWR8ZW58MHx8fHwxNzcxOTEzMjU1fDA&ixlib=rb-4.1.0&q=85' },
    { range: '125-250 kVA', applications: 'Industries, Hospitals', image: 'https://images.unsplash.com/photo-1765897569589-effcccec14d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZGllc2VsJTIwZ2VuZXJhdG9yJTIwcG93ZXIlMjBwbGFudCUyMHNpdGV8ZW58MHx8fHwxNzcxOTEzMjM2fDA&ixlib=rb-4.1.0&q=85' },
  ];

  return (
    <div className="pt-20" data-testid="home-page">
      <section className="hero-gradient relative overflow-hidden noise-overlay" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="hero-title">
                Reliable Power Solutions You Can Trust
              </h1>
              <p className="text-lg sm:text-xl mb-8 text-slate-200" data-testid="hero-subtitle">
                Sales, Installation & Maintenance of Diesel Generators from 5 kVA to 250 kVA
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-3 px-8 rounded-sm transition-all uppercase tracking-wide inline-flex items-center justify-center gap-2"
                  data-testid="hero-cta-quote"
                >
                  Get a Quote <ArrowRight size={20} />
                </Link>
                <Link
                  to="/products"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#1B365D] font-semibold py-3 px-8 rounded-sm transition-all uppercase tracking-wide inline-flex items-center justify-center"
                  data-testid="hero-cta-products"
                >
                  View Products
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1765897569589-effcccec14d4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZGllc2VsJTIwZ2VuZXJhdG9yJTIwcG93ZXIlMjBwbGFudCUyMHNpdGV8ZW58MHx8fHwxNzcxOTEzMjM2fDA&ixlib=rb-4.1.0&q=85"
                alt="Industrial Generator"
                className="rounded-sm shadow-2xl"
                data-testid="hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1B365D] mb-4" data-testid="why-choose-us-title">
              Why Choose Us?
            </h2>
            <p className="text-lg text-slate-600">Your trusted partner for reliable power solutions</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 hover:shadow-lg transition-all p-8 rounded-sm group"
                data-testid={`feature-card-${index}`}
              >
                <div className="text-[#F97316] mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-[#1B365D] mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1B365D] mb-4" data-testid="services-title">
              Our Services
            </h2>
            <p className="text-lg text-slate-600">Comprehensive generator solutions for your business</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#F97316] hover:shadow-xl transition-all group"
                data-testid={`service-card-${index}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1B365D] mb-3">{service.title}</h3>
                  <p className="text-slate-600 mb-4">{service.description}</p>
                  <Link
                    to="/services"
                    className="text-[#F97316] font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all"
                  >
                    Learn More <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-testid="products-preview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1B365D] mb-4" data-testid="products-title">
              Generator Capacity Range
            </h2>
            <p className="text-lg text-slate-600">Find the perfect generator for your requirements</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {productCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#F97316] hover:shadow-xl transition-all"
                data-testid={`product-category-${index}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.range}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-2xl font-bold">{category.range}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-slate-600 mb-4">
                    <span className="font-semibold">Perfect for:</span> {category.applications}
                  </p>
                  <Link
                    to="/products"
                    className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-2 px-6 rounded-sm transition-colors uppercase tracking-wide inline-flex items-center gap-2 text-sm"
                  >
                    View Details <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="py-20 bg-slate-50" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-[#1B365D] mb-4" data-testid="testimonials-title">
                What Our Clients Say
              </h2>
              <p className="text-lg text-slate-600">Trusted by businesses across Karnataka</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {Array.isArray(testimonials) && testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white p-8 rounded-sm border border-slate-200 shadow-md"
                  data-testid={`testimonial-card-${index}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="fill-[#F97316] text-[#F97316]" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold text-[#1B365D]">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.company}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 bg-[#1B365D] text-white noise-overlay" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6" data-testid="cta-title">
              Ready to Power Your Business?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Contact us today for a free consultation and quote on our diesel generator solutions.
            </p>
            <Link
              to="/contact"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-4 px-10 rounded-sm transition-all uppercase tracking-wide inline-flex items-center gap-2"
              data-testid="cta-button"
            >
              Contact Us Now <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
